import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Loader } from '@googlemaps/js-api-loader';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import {
  ApiService,
  StreetHouseRow,
  StreetLineItem,
} from '../../../shared/services/api.service';
import {
  StreetSummaryDialogComponent,
  StreetSummaryDialogData,
  StreetSummaryDialogResult,
} from './street-summary-dialog.component';

type StreetLineMeta = Pick<StreetLineItem, 'streetKey' | 'streetName' | 'suburbName' | 'roadType'>;

@Component({
  selector: 'app-street-search',
  templateUrl: './street-search.component.html',
  styleUrls: ['./street-search.component.css'],
})
export class StreetSearchComponent implements OnInit, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef<HTMLDivElement>;

  isLoading = true;
  loadingMessage = 'Loading street lines...';
  streetCount = 0;
  lineSegmentCount = 0;
  selectedStreetLabel = '';
  housesForSelectedStreet: StreetHouseRow[] = [];

  private static mapLoadPromise: Promise<unknown> | null = null;
  private map: any;
  private polylines: any[] = [];
  private selectedPolylines: any[] = [];
  private readonly defaultStrokeColor = '#1E88E5';
  private readonly selectedStrokeColor = '#FF8F00';

  constructor(
    private loader: Loader,
    private apiService: ApiService,
    private dialog: MatDialog,
    private router: Router,
    private snackBar: MatSnackBar
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      await this.ensureGoogleMapsLoaded();
      this.initializeMap();
      await this.loadStreetLines();
    } catch (error) {
      console.error('Failed to initialize street search map:', error);
      this.loadingMessage = 'Failed to load map data.';
    } finally {
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.clearPolylines();
  }

  private async ensureGoogleMapsLoaded(): Promise<void> {
    if (!StreetSearchComponent.mapLoadPromise) {
      StreetSearchComponent.mapLoadPromise = this.loader.load();
    }
    await StreetSearchComponent.mapLoadPromise;
  }

  private initializeMap(): void {
    const MapsApi = google.maps as any;
    this.map = new MapsApi.Map(this.mapContainer.nativeElement, {
      center: { lat: -33.86, lng: 18.63 },
      zoom: 11,
      mapTypeId: 'roadmap',
      streetViewControl: false,
      fullscreenControl: true,
      mapTypeControl: true,
      gestureHandling: 'greedy',
    });
  }

  private async loadStreetLines(): Promise<void> {
    try {
      this.loadingMessage = 'Loading street lines...';
      const response = await firstValueFrom(this.apiService.listStreetLines());
      const rows = response.streets || [];
      this.lineSegmentCount = rows.length;
      this.streetCount = new Set(
        rows.map((row) => `${row.suburbName.toUpperCase()}::${row.streetName.toUpperCase()}`)
      ).size;

      this.clearPolylines();
      this.renderStreetLines(rows);
    } catch (error) {
      console.error('Failed to load street lines:', error);
      const anyError = error as any;
      const message =
        anyError?.error?.error?.details ||
        anyError?.error?.error?.message ||
        'Failed to load street lines';
      this.snackBar.open(message, 'Close', { duration: 6500 });
    }
  }

  private clearPolylines(): void {
    this.polylines.forEach((line) => line.setMap(null));
    this.polylines = [];
    this.clearSelectedStreetStyling();
  }

  private clearSelectedStreetStyling(): void {
    this.selectedPolylines.forEach((line) =>
      line.setOptions({
        strokeColor: this.defaultStrokeColor,
        strokeWeight: 2.5,
        zIndex: 1,
      })
    );
    this.selectedPolylines = [];
  }

  private renderStreetLines(rows: StreetLineItem[]): void {
    if (!this.map) {
      return;
    }

    const MapsApi = google.maps as any;
    const bounds = new MapsApi.LatLngBounds();
    const streetIndex = new Map<string, any[]>();

    rows.forEach((row) => {
      const segments = this.toPathSegments(row.geojson);
      const lineMeta: StreetLineMeta = {
        streetKey: row.streetKey,
        streetName: row.streetName,
        suburbName: row.suburbName,
        roadType: row.roadType,
      };

      segments.forEach((segment) => {
        if (!segment.length) return;

        const line = new MapsApi.Polyline({
          path: segment,
          map: this.map,
          clickable: true,
          strokeColor: this.defaultStrokeColor,
          strokeOpacity: 0.85,
          strokeWeight: 2.5,
          zIndex: 1,
        });

        const bucketKey = `${lineMeta.suburbName.toUpperCase()}::${lineMeta.streetName.toUpperCase()}`;
        const existing = streetIndex.get(bucketKey) || [];
        existing.push(line);
        streetIndex.set(bucketKey, existing);

        line.addListener('click', () => {
          void this.onStreetClicked(lineMeta, streetIndex.get(bucketKey) || []);
        });

        segment.forEach((point) => bounds.extend(point));
        this.polylines.push(line);
      });
    });

    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds);
    }
  }

  private toPathSegments(
    geojson: StreetLineItem['geojson']
  ): Array<Array<{ lat: number; lng: number }>> {
    if (geojson.type === 'LineString') {
      return [
        (geojson.coordinates as number[][]).map(([lng, lat]) => ({ lat, lng })),
      ];
    }
    return (geojson.coordinates as number[][][]).map((segment) =>
      segment.map(([lng, lat]) => ({ lat, lng }))
    );
  }

  private async onStreetClicked(meta: StreetLineMeta, lines: any[]): Promise<void> {
    try {
      this.clearSelectedStreetStyling();
      this.selectedPolylines = lines;
      lines.forEach((line) =>
        line.setOptions({
          strokeColor: this.selectedStrokeColor,
          strokeWeight: 4.2,
          zIndex: 10,
        })
      );

      this.selectedStreetLabel = `${meta.streetName}, ${meta.suburbName}`;
      const housesResponse = await firstValueFrom(
        this.apiService.listStreetHouses(meta.streetName, meta.suburbName)
      );
      this.housesForSelectedStreet = housesResponse.rows;

      const dialogRef = this.dialog.open<
        StreetSummaryDialogComponent,
        StreetSummaryDialogData,
        StreetSummaryDialogResult | undefined
      >(StreetSummaryDialogComponent, {
        width: '440px',
        data: {
          streetName: meta.streetName,
          suburbName: meta.suburbName,
          roadType: meta.roadType || null,
          houseCount: housesResponse.houseCount,
        },
      });

      const result = await firstValueFrom(dialogRef.afterClosed());
      if (result?.action === 'viewStreetInfo') {
        await this.router.navigate(['/admin/street-search/info'], {
          queryParams: {
            streetName: meta.streetName,
            suburbName: meta.suburbName,
          },
        });
      }
    } catch (error) {
      console.error('Failed to fetch street details:', error);
      this.snackBar.open('Failed to load street details', 'Close', { duration: 4500 });
    }
  }
}
